
import { ChevronRight } from "lucide-react";

import { SpaceImage, SpaceLocation, getSpaceLocation } from "~/components/shared/Space";
import { getCartPrice } from "~/lib/cart";
import { cn, currency } from "~/lib/utils";
import { type CartWithPackagesAndSpaces } from "~/models";
import { InfoTooltip } from "./InfoTooltip";
import DATA from "~/data";
import { CandleIcon } from "~/icons";

export function InYourOrder({ cart }: { cart: CartWithPackagesAndSpaces }) {
    return (
        <section className="flex flex-col gap-4">
            <h2 className="text-2xl font-title">In your order</h2>
            {cart.packages.map((pkg) => (
                <PackageSummary key={pkg.id} cartPackage={pkg} />
            ))}
            {cart.spaces.map((space) => (
                <SpaceSummary key={space.id} cartSpace={space} />
            ))}
        </section>
    );
}

function PackageSummary({ cartPackage }: { cartPackage: CartPackage }) {
    const { customerSelection, packageData } = cartPackage;

    const container = packageData.find(({ field }) => "containers-tier" === field);
    const urn = packageData
        .find(({ field }) => "urn-allowance" === field)
        ?.items.find(({ value }) => value === customerSelection.urn);

    const images = [];

    if (container && container.image) {
        images.push(container.image);
    }

    if (urn && urn.image) {
        images.push(urn.image);
    }

    if (customerSelection?.container) {
        const packageDataWithItems = packageData.find((item) => item.items);
        const container = packageDataWithItems.items?.find((item: { name: string }) => item.name === customerSelection.container);
        if (container?.image) {
            images.push(container?.image);
        }
    }

    console.log("images ", images);

    return (
        <div className="flex items-center gap-6 border-b pb-4">
            <div
                className={cn(
                    "w-20 h-20 flex justify-center items-center p-2 border-4 border-gray-100",
                    images.length < 1 && "bg-gray-100",
                )}
            >
                {images.length > 0 ? (
                    images.map((image, index) => <img key={index} src={image} className="object-contain w-1/2 h-1/2" />)
                ) : (
                    <CandleIcon className="w-8 h-8" />
                )}
            </div>
            <hgroup className="flex flex-col gap-2">
                <h2 className="font-semibold">{cartPackage.salePackage.name}</h2>
                <p className="text-sm">{currency(cartPackage.amounts.subtotal, { minimumFractionDigits: 2 })}</p>
            </hgroup>
        </div>
    );
}

function SpaceSummary({ cartSpace }: { cartSpace: CartSpaceWithSpace }) {
    const { space } = cartSpace;

    // const section = space.section?.length === 1 ? `Section ${space.section}` : "cool";
    return (
        <div className="flex items-center gap-6 border-b pb-4">
            <SpaceImage src={space.thumbnail} className="w-20 !h-20" size="sm" />
            <section className="flex flex-1 flex-col gap-2">
                <h2 className="capitalize font-semibold">{space.section}</h2>
                <section className="flex flex-row-reverse justify-end gap-2">
                    <SpaceLocation {...space} />
                </section>
                {space.price ? (
                    <h2 className="text-sm">{currency(cartSpace.amounts.subtotal, { minimumFractionDigits: 2 })}</h2>
                ) : null}
            </section>
        </div>
    );
}

export function PaymentSummary({
    cart,
    title = "Payment Summary",
}: {
    title?: string;
    cart: CartWithPackagesAndSpaces;
}) {
    const { packages, spaces } = cart;
    const cartAmounts = getCartPrice(cart);

    const cartNotes = DATA?.cart?.notes;


    return (
        <section className="flex flex-col gap-4">
            <h2 className="text-2xl font-title">{title}</h2>
            <ul className="flex flex-col gap-4">
                <li>
                    <details>
                        <summary className="flex justify-between cursor-pointer">
                            <div className="flex items-center gap-1">
                                <ChevronRight className="" width="1em" height="1em" />
                                <div>Subtotal</div>
                                {cartNotes?.subtotal && <InfoTooltip content={cartNotes?.subtotal} />}
                            </div>
                            <CartItemSummaryCurrency value={cartAmounts.subtotal} />
                        </summary>
                        <ul className="pl-6 text-sm border-b border-border pb-4">
                            {packages.map((pkg) => {
                                return (
                                    <li key={pkg.id} className="flex justify-between">
                                        <div>{pkg.salePackage.name}</div>
                                        <div>{currency(pkg.amounts?.subtotal ?? 0, { minimumFractionDigits: 2 })}</div>
                                    </li>
                                );
                            })}
                            {spaces.map((cartSpace) => {
                                return (
                                    <li key={cartSpace.id} className="flex justify-between">
                                        <div className="font-medium">{getSpaceLocation(cartSpace.space)}</div>
                                        <div>{currency(cartSpace.amounts.subtotal, { minimumFractionDigits: 2 })}</div>
                                    </li>
                                );
                            })}
                        </ul>
                    </details>
                </li>
                <li>
                    <Details>
                        <summary className="flex justify-between cursor-pointer">
                            <div className="flex items-center gap-1">
                                <ChevronRight className="" width="1em" height="1em" />
                                <div>Taxes</div>
                            </div>
                            <CartItemSummaryCurrency value={cartAmounts.taxAmount ?? 0} />
                        </summary>
                        <ul className="pl-6 text-sm border-b border-border pb-4">
                            {cart.packages.map((pkg) => {
                                return (
                                    <CartItemBreakdown
                                        key={pkg.id}
                                        title={pkg.salePackage.name}
                                        items={pkg.amounts.tax}
                                    />
                                );
                            })}
                            {cart.spaces.map((cartSpace) => {
                                return (
                                    <CartItemBreakdown
                                        key={cartSpace.id}
                                        title={getSpaceLocation(cartSpace.space)}
                                        items={cartSpace.amounts.tax}
                                    />
                                );
                            })}
                        </ul>
                    </Details>
                </li>
                <li>
                    <Details>
                        <summary className="flex justify-between cursor-pointer">
                            <div className="flex items-center gap-1">
                                <ChevronRight className="" width="1em" height="1em" />
                                <div>Fees</div>
                            </div>
                            <CartItemSummaryCurrency value={cartAmounts.feeAmount ?? 0} />
                        </summary>
                        <ul className="pl-6 text-sm">
                            {cart.packages.map((pkg) => {
                                return (
                                    <CartItemBreakdown
                                        key={pkg.id}
                                        title={pkg.salePackage.name}
                                        items={pkg.amounts.fee}
                                    />
                                );
                            })}
                            {cart.spaces.map((cartSpace) => {
                                return (
                                    <CartItemBreakdown
                                        key={cartSpace.id}
                                        title={getSpaceLocation(cartSpace.space)}
                                        items={cartSpace.amounts.fee}
                                    />
                                );
                            })}
                            <CartItemBreakdown title="Per order" items={cartAmounts.fee} />
                        </ul>
                    </Details>
                </li>
            </ul>
            <div className="flex justify-between border-t border-b border-border py-4">
                <div>Total</div>
                <CartItemSummaryCurrency value={cartAmounts.total} />
            </div>
        </section>
    );
}

function Details({ className, ...props }: React.ComponentProps<"details">) {
    return <details className={cn(className, "open:[&>summary>div>svg]:rotate-90")} {...props} />;
}

function CartItemBreakdown({
    title,
    items,
}: {
    title: string;
    items: { name: string; value: number; description?: string; tooltip?: string }[];
}) {
    if (items.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2">
            <section className="flex items-center gap-2">
                <div className="font-medium">{title}</div>
                {items.find(({ tooltip }) => tooltip) && <InfoTooltip content={items.find(({ tooltip }) => tooltip)?.tooltip} />}
            </section>
            <ul className="ml-6">
                {items.map(({ name, value, description }, index) => (
                    <li key={index} className="flex justify-between">
                        <div>
                            {name} <em>{description}</em>
                        </div>

                        <div>{currency(value, { minimumFractionDigits: 2 })}</div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function CartItemSummaryCurrency({ value }: { value: number }) {
    return <div className="font-medium text-lg">{currency(value, { minimumFractionDigits: 2 })}</div>;
}
